/**
 * Header.tsx — Shell-Bar actions for the prince-ui AppShell.
 *
 * Exports two composable pieces:
 *  - `HeaderActions`  – release picker + theme picker + data-reload button
 *                       (used in AppShell `items` prop)
 *  - `HeaderUserSlot` – avatar span for the `user` prop + user-menu popover logic
 *
 * The Popover/List/Avatar UI5 components are replaced by native HTML + CSS
 * variables; this file has no UI5 component imports.
 */

import { useEffect, useRef, useState } from "react";
import { Icon, Segment, SegmentedControl } from "@conuti-das/prince-ui";
import { useAppTheme } from "./ThemeContext";
import { useRelease, releaseLabel, releaseShortLabel } from "@/context";
import { useIdentity } from "@/hooks/useIdentity";
import { useTokenExpiry } from "@/hooks/useTokenExpiry";
import { getUser, getAccountUrl, logout } from "@/lib/auth";
import summary from "@/data/generated/summary.json";
import { ALL, type ReleaseKey } from "@/types/pipeline";

const generated = (summary as { generated_at: string }).generated_at;
const generatedLocal = generated
  ? new Date(generated).toLocaleString("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    })
  : "?";

function initials(name: string | null | undefined): string {
  if (!name?.trim()) return "?";
  return name
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

// ── Formatversion ───────────────────────────────────────────────────────────

/**
 * Ab wie vielen Schaltflächen (inkl. „Alle“) und ab welcher Fensterbreite die
 * Segmentleiste in ein Menü klappt. Die Kopfzeile trägt schon Build-Zeit,
 * Theme, Reload und Avatar — mehr als vier Segmente sprengen sie, und mit jedem
 * neuen Stand kommt eines dazu.
 */
const SEGMENT_MAX = 4;
const SEGMENT_MIN_WIDTH = 1120;

/** Fensterbreite als Zustand — ohne CSS-Datei und ohne neue Abhängigkeit. */
function useWideViewport(min: number): boolean {
  const [wide, setWide] = useState(
    () => typeof window === "undefined" || window.innerWidth >= min,
  );
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= min);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [min]);
  return wide;
}

/**
 * Globale Formatversions-Auswahl.
 *
 * Breit und kurz: prince-uis `SegmentedControl` — alle Stände sichtbar, eine
 * Wahl ist ein Klick. Schmal oder ab fünf Einträgen: dasselbe Angebot als
 * kompaktes Menü, gebaut wie der Theme-Picker daneben, damit die Kopfzeile
 * eine Handschrift behält. Der Umschaltpunkt ist eine Frage der Breite, nicht
 * des Geschmacks: Segmente wachsen mit jedem Release, die Kopfzeile nicht.
 */
export function ReleaseControl() {
  const { release, setRelease, releases } = useRelease();
  const [open, setOpen] = useState(false);
  const options: ReleaseKey[] = [ALL, ...releases];
  const wide = useWideViewport(SEGMENT_MIN_WIDTH);
  const asSegments = wide && options.length <= SEGMENT_MAX;

  if (asSegments) {
    return (
      <SegmentedControl
        aria-label="Formatversion"
        size="s"
        selectedKeys={new Set([release])}
        onSelectionChange={(keys) => {
          const first = [...keys][0];
          if (typeof first === "string") setRelease(first);
        }}
      >
        {options.map((r) => (
          <Segment key={r} id={r} aria-label={releaseLabel(r)}>
            {releaseShortLabel(r)}
          </Segment>
        ))}
      </SegmentedControl>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        aria-label="Formatversion auswählen"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Formatversion: ${releaseLabel(release)}`}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 8px",
          background: "none",
          border: "1px solid var(--prn-separator)",
          borderRadius: 8,
          color: "var(--prn-label)",
          fontFamily: "var(--prn-font)",
          fontSize: 13,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {/* Kein Symbol: Die Kurzform „FV2610“ ist selbst die Beschriftung —
            ein Piktogramm für „Formatversion“ gibt es im Satz nicht. */}
        {releaseShortLabel(release)}
        <Icon name="chevron-down" size={13} />
      </button>

      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            aria-label="Formatversion"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              zIndex: 1000,
              minWidth: 220,
              background: "var(--prn-fill-elevated)",
              border: "1px solid var(--prn-separator)",
              borderRadius: 10,
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
              padding: "6px 0",
              fontFamily: "var(--prn-font)",
            }}
          >
            <div
              style={{
                padding: "4px 14px 8px",
                fontSize: 11,
                fontWeight: 600,
                color: "var(--prn-label-2)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Formatversion
            </div>
            {options.map((r) => (
              <button
                key={r}
                type="button"
                role="menuitemradio"
                aria-checked={r === release}
                onClick={() => {
                  setRelease(r);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "7px 14px",
                  background: "none",
                  border: "none",
                  color: "var(--prn-label)",
                  fontFamily: "var(--prn-font)",
                  fontSize: 13,
                  cursor: "pointer",
                  textAlign: "left",
                  fontWeight: r === release ? 600 : 400,
                  whiteSpace: "nowrap",
                }}
              >
                {r === release ? (
                  <Icon name="check" size={14} />
                ) : (
                  <span style={{ display: "inline-block", width: 14 }} />
                )}
                {releaseLabel(r)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Release picker + theme picker + refresh button, rendered as react nodes for
 * the AppShell `actions` slot.
 */
export function HeaderActions() {
  const { theme, setTheme, themes } = useAppTheme();
  const [themeOpen, setThemeOpen] = useState(false);
  const themeRef = useRef<HTMLDivElement>(null);

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {/* Globale Formatversion — schlägt auf alle Sichten durch */}
      <ReleaseControl />

      {/* Build timestamp */}
      <span
        style={{
          fontSize: 12,
          color: "var(--prn-label-2)",
          fontFamily: "var(--prn-font)",
          whiteSpace: "nowrap",
        }}
      >
        Build: {generatedLocal}
      </span>

      {/* Theme picker */}
      <div ref={themeRef} style={{ position: "relative" }}>
        <button
          type="button"
          aria-label="Theme auswählen"
          title="Erscheinungsbild"
          onClick={() => setThemeOpen((o) => !o)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 8px",
            background: "none",
            border: "1px solid var(--prn-separator)",
            borderRadius: 8,
            color: "var(--prn-label)",
            fontFamily: "var(--prn-font)",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          <Icon name="droplet" size={15} />
          <Icon name="chevron-down" size={13} />
        </button>

        {themeOpen && (
          <>
            {/* Backdrop to close on outside click */}
            <div
              style={{ position: "fixed", inset: 0, zIndex: 999 }}
              onClick={() => setThemeOpen(false)}
            />
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                zIndex: 1000,
                minWidth: 200,
                background: "var(--prn-fill-elevated)",
                border: "1px solid var(--prn-separator)",
                borderRadius: 10,
                boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                padding: "6px 0",
                fontFamily: "var(--prn-font)",
              }}
            >
              <div
                style={{
                  padding: "4px 14px 8px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--prn-label-2)",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Erscheinungsbild
              </div>
              {themes.map((t) => (
                <button
                  key={t.themeName}
                  type="button"
                  onClick={() => {
                    setTheme(t.themeName);
                    setThemeOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "7px 14px",
                    background: "none",
                    border: "none",
                    color: "var(--prn-label)",
                    fontFamily: "var(--prn-font)",
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                    fontWeight: t.themeName === theme ? 600 : 400,
                  }}
                >
                  {t.themeName === theme && (
                    <Icon name="check" size={14} />
                  )}
                  {t.themeName !== theme && (
                    <span style={{ display: "inline-block", width: 14 }} />
                  )}
                  {t.displayName}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Data reload */}
      <button
        type="button"
        aria-label="Daten neu laden"
        title="Daten neu laden"
        onClick={() => window.location.reload()}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          background: "none",
          border: "none",
          borderRadius: 8,
          color: "var(--prn-label)",
          cursor: "pointer",
        }}
      >
        <Icon name="bolt" size={16} />
      </button>
    </div>
  );
}

/**
 * User avatar slot + floating profile menu. Rendered as the `user` prop of
 * prince AppShell; the wrapping `onProfileClick` button is provided by AppShell
 * itself, so we expose the avatar separately from the menu.
 */
export function HeaderUserSlot() {
  // Bootstrap X-Actor from Keycloak (side-effect only).
  useIdentity();
  const kcUser = getUser();
  const abbr = initials(kcUser?.name);

  return (
    <span
      aria-hidden
      title={kcUser?.name ?? "Profil"}
      style={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: "var(--prn-accent-soft)",
        color: "var(--prn-label)",
        fontFamily: "var(--prn-font)",
        fontSize: 12,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
      }}
    >
      {abbr}
    </span>
  );
}

/**
 * Floating user/profile menu, placed as an overlay in AppShell.tsx so it can
 * be positioned relative to the user button in the shell bar.
 */
export function UserMenu({ onClose }: { onClose: () => void }) {
  useIdentity();
  const kcUser = getUser();
  const accountUrl = getAccountUrl();
  const { secondsLeft, label: expiryLabel } = useTokenExpiry();
  const abbr = initials(kcUser?.name);

  const expiryColor =
    secondsLeft === null
      ? "var(--prn-label-2)"
      : secondsLeft < 60
      ? "var(--sapNegativeColor, #bb0000)"
      : secondsLeft < 300
      ? "var(--sapCriticalColor, #e76500)"
      : "var(--prn-label-2)";

  return (
    <>
      {/* Backdrop */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 999 }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed",
          top: 52,
          right: 12,
          zIndex: 1000,
          minWidth: 260,
          background: "var(--prn-fill-elevated)",
          border: "1px solid var(--prn-separator)",
          borderRadius: 12,
          boxShadow: "0 4px 32px rgba(0,0,0,0.18)",
          padding: "14px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          fontFamily: "var(--prn-font)",
        }}
      >
        {/* User info */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              background: "var(--prn-accent-soft)",
              color: "var(--prn-label)",
              fontSize: 14,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {abbr}
          </span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: "var(--prn-label)" }}>
              {kcUser?.name ?? "Anonymer Nutzer"}
            </span>
            {kcUser?.email && (
              <span style={{ fontSize: 12, color: "var(--prn-label-2)" }}>
                {kcUser.email}
              </span>
            )}
          </div>
        </div>

        {expiryLabel && (
          <div style={{ fontSize: 12, color: expiryColor }}>
            Session läuft ab in <strong>{expiryLabel}</strong>
          </div>
        )}

        {/* Actions */}
        <div
          style={{
            borderTop: "1px solid var(--prn-separator)",
            paddingTop: 10,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          {accountUrl && (
            <button
              type="button"
              onClick={() => {
                window.open(accountUrl, "_blank", "noopener,noreferrer");
                onClose();
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                background: "none",
                border: "1px solid var(--prn-separator)",
                borderRadius: 8,
                color: "var(--prn-label)",
                fontFamily: "var(--prn-font)",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              <Icon name="settings" size={14} />
              Mein Profil
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onClose();
              logout();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: "var(--prn-destructive, #cc0000)",
              border: "none",
              borderRadius: 8,
              color: "#fff",
              fontFamily: "var(--prn-font)",
              fontSize: 13,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            <Icon name="x" size={14} />
            Abmelden
          </button>
        </div>
      </div>
    </>
  );
}
